import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SaveAltOutlinedIcon from "@mui/icons-material/SaveAltOutlined";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import Breakpoint from "@entrilia/shared/components/Breakpoint";
import { makeTintedBackgroundFromColor } from "@entrilia/shared/utilities/colorHelper";
import { getFileExtension, isFileDownloadAllowed } from "@entrilia/shared/utilities/fileHelper";
import type { Attachment } from "../../../store/state/messages/types";

interface Props {
  attachment: Attachment;
  onDownload: () => void;
  onPreview: () => void;
}

const AttachmentComponent = ({ attachment, onDownload, onPreview }: Props) => {
  const { fileName } = attachment;
  const extension = getFileExtension(fileName);
  const nameWithoutExtension = fileName.slice(0, fileName.length - extension.length);
  const canDownload = isFileDownloadAllowed(attachment.accessOptions);

  return (
    <Box
      sx={{
        gap: 2.5,
        display: "grid",
        gridTemplateColumns: canDownload ? "auto minmax(0, auto) auto" : "auto minmax(0, auto)",
        alignItems: "center",
        justifyContent: "start",
      }}
    >
      <Box
        sx={[
          { display: "flex", width: "100%", alignItems: "center", justifyContent: "center" },
          (theme) => ({
            backgroundColor: makeTintedBackgroundFromColor(theme, theme.palette.info.main),
            borderRadius: "50%",
            height: "3rem",
            width: "3rem",
          }),
        ]}
      >
        <DescriptionOutlinedIcon fontSize="medium" color="info" />
      </Box>
      <Box onClick={onPreview}>
        <Stack direction="row" sx={{ "&:hover": { cursor: "pointer", textDecoration: "underline" } }}>
          <Typography noWrap>{nameWithoutExtension}</Typography>
          <Typography sx={{ flexShrink: 0 }}>{extension}</Typography>
        </Stack>
        <Typography color="textSecondary">{attachment.fileSizeAsString}</Typography>
      </Box>
      {canDownload && (
        <>
          <Breakpoint from="sm">
            <Button variant="outlined" color="secondary" onClick={onDownload}>
              Download
            </Button>
          </Breakpoint>
          <Breakpoint to="sm">
            <IconButton
              onClick={(evt) => {
                evt.preventDefault();
                evt.stopPropagation();
                onDownload();
              }}
            >
              <SaveAltOutlinedIcon />
            </IconButton>
          </Breakpoint>
        </>
      )}
    </Box>
  );
};

export default AttachmentComponent;
